import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from '../config';
import { useCart } from "../CartContext";

const US_STATE_ABBR = new Set([
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
  "DC", "PR", "GU", "VI", "MP", "AS"
]);

const ZIP_RANGES = [
  { state: "AL", min: 35004, max: 36925 },
  { state: "AK", min: 99501, max: 99950 },
  { state: "AZ", min: 85001, max: 86556 },
  { state: "AR", min: 71601, max: 72959 },
  { state: "CA", min: 90001, max: 96162 },
  { state: "CO", min: 80001, max: 81658 },
  { state: "CT", min: 6001, max: 6928 },
  { state: "DE", min: 19701, max: 19980 },
  { state: "FL", min: 32003, max: 34997 },
  { state: "GA", min: 30002, max: 39901 },
  { state: "HI", min: 96701, max: 96898 },
  { state: "ID", min: 83201, max: 83876 },
  { state: "IL", min: 60001, max: 62999 },
  { state: "IN", min: 46001, max: 47997 },
  { state: "IA", min: 50001, max: 52809 },
  { state: "KS", min: 66002, max: 67954 },
  { state: "KY", min: 40003, max: 42788 },
  { state: "LA", min: 70001, max: 71497 },
  { state: "ME", min: 3901, max: 4992 },
  { state: "MD", min: 20601, max: 21930 },
  { state: "MA", min: 1001, max: 5544 },
  { state: "MI", min: 48001, max: 49971 },
  { state: "MN", min: 55001, max: 56763 },
  { state: "MS", min: 38601, max: 39776 },
  { state: "MO", min: 63001, max: 65899 },
  { state: "MT", min: 59001, max: 59937 },
  { state: "NE", min: 68001, max: 69367 },
  { state: "NV", min: 88901, max: 89883 },
  { state: "NH", min: 3031, max: 3897 },
  { state: "NJ", min: 7001, max: 8989 },
  { state: "NM", min: 87001, max: 88439 },
  { state: "NY", min: 10001, max: 14975 },
  { state: "NC", min: 27006, max: 28909 },
  { state: "ND", min: 58001, max: 58856 },
  { state: "OH", min: 43001, max: 45999 },
  { state: "OK", min: 73001, max: 74966 },
  { state: "OR", min: 97001, max: 97920 },
  { state: "PA", min: 15001, max: 19640 },
  { state: "RI", min: 2801, max: 2940 },
  { state: "SC", min: 29001, max: 29948 },
  { state: "SD", min: 57001, max: 57799 },
  { state: "TN", min: 37010, max: 38589 },
  { state: "TX", min: 75001, max: 88595 },
  { state: "UT", min: 84001, max: 84791 },
  { state: "VT", min: 5001, max: 5907 },
  { state: "VA", min: 20101, max: 24658 },
  { state: "WA", min: 98001, max: 99403 },
  { state: "WV", min: 24701, max: 26886 },
  { state: "WI", min: 53001, max: 54990 },
  { state: "WY", min: 82001, max: 83414 },
];
const PACKAGING_OZ = 4;

const brownsvilleRadiusZipCodes = [
  "78520", "78521", "78522", "78523", "78526", // Brownsville
  "78575", // Olmito
  "78566", // Los Fresnos
  "78586", // San Benito
  "78578", // Port Isabel
  "78583", // Rio Hondo
  "78550", "78551", "78552", // Harlingen
  "78568", // Lozano
  "78597", // South Padre Island
  "78535"  // Combes
];

function stateFromZip(zip) {
  const z = Number(String(zip).slice(0, 5));
  if (!Number.isFinite(z)) return null;

  const match = ZIP_RANGES.find(r => z >= r.min && z <= r.max);
  return match?.state ?? null;
}

function normalizeZip(zip) {
  const m = String(zip || "").trim().match(/\d{5}/);
  return m ? m[0] : "";
}

function isValidUSState(state) {
  const s = String(state || "").trim().toUpperCase();
  return US_STATE_ABBR.has(s);
}

function isValidZip(zip) {
  return /^\d{5}(-\d{4})?$/.test(String(zip || "").trim());
}

export default function EnterShipping() {
  const navigate = useNavigate();
  const { cart } = useCart();

  const itemsForEstimate = useMemo(
    () => cart.map((i) => ({ _id: i._id || i.id, quantity: i.quantity })),
    [cart]
  );

  const [address, setAddress] = useState({
    name: "",
    line1: "",
    line2: "",
    city: "",
    state: "",
    zip: "",
    country: "US",
  });

  const [loading, setLoading] = useState(false);
  const [quoteId, setQuoteId] = useState(null);
  const [options, setOptions] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [error, setError] = useState(null);
  const [eligibleFreeShipping, setEligibleFreeShipping] = useState(false);
  const [freeShippingID, setFreeShippingID] = useState(null);
  const [authResponse, setAuthResponse] = useState(null);
  const [userResponse, setUserResponse] = useState(null);

  function update(k) {
    return (e) => setAddress((a) => ({ ...a, [k]: e.target.value }));
  }

  function validateForm() {
    if (!address.name.trim()) return "Name is required";
    if (!address.line1.trim()) return "Address line 1 is required";
    if (!address.city.trim()) return "City is required";
    if (!isValidUSState(address.state)) return "State must be a 2-letter code (e.g., TX)";
    if (!isValidZip(address.zip)) return "ZIP must be 5 digits (or ZIP+4)";
    const zipState = stateFromZip(address.zip);
    if (zipState && zipState !== address.state.toUpperCase()) {
      console.log("ZIP/state mismatch:", zipState, address.state);
      return "ZIP/state mismatch";
    }
    return null;
  }

  async function handleEstimate(e) {
    e?.preventDefault();
    const msg = validateForm();
    if (msg) return setError(msg);

    setError(null);
    setLoading(true);
    setOptions([]);
    setQuoteId(null);
    setSelectedId(null);

    try {
      let weightSum = 0;
      let totalBasePrice = 0;
      for (const cartItem of cart) {
        await axios.get(`${API_BASE_URL}/api/products/${cartItem["_id"]}`)
          .then((response) => {
            const product = response.data;
            if (product && product?.dimensions?.["Weight (oz)"]) {
              weightSum += parseFloat(product.dimensions["Weight (oz)"]["$numberDecimal"]) * cartItem.quantity;
            } else {
              weightSum += 16 * cartItem.quantity; // default to 1 lb (16 oz) if no weight provided
            }
            if (product && product.price) {
              totalBasePrice += product.price * cartItem.quantity;
            } else {
              totalBasePrice += 0;
            }
          });
      }
      weightSum = Math.max(1, Math.ceil(weightSum + PACKAGING_OZ));
      totalBasePrice * 100 > 4999 ? setEligibleFreeShipping(true) : setEligibleFreeShipping(false);
      const payload = {
        items: itemsForEstimate,
        address: {
          ...address,
          state: address.state.trim().toUpperCase(),
          zip: normalizeZip(address.zip),
          country: "US",
        },
        parcel: {
          weight: weightSum || 160,
          mass_unit: "oz",
        }
      };

      const res = await axios.post(`${API_BASE_URL}/api/shipping/estimate`, payload, {
        withCredentials: true,
      });

      const qid = res.data?.quoteId;
      const opts = res.data?.options || [];
      if (!qid || opts.length === 0) {
        setError("No shipping rates available for that address. Please double-check it.");
        return;
      }
      const sortedOpts = Object.values(
        opts.reduce((acc, item) => {
          const label = item.label;
          if (!label) return acc;

          const nextAmt = Number(item.amount);
          const curr = acc[label];
          const currAmt = curr ? Number(curr.amount) : Infinity;

          if (!curr || nextAmt < currAmt) acc[label] = item;
          return acc;
        }, {})
      ).sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
      );

      if (totalBasePrice * 100 > 4999 && sortedOpts.length) {
        let lowestIdx = 0;
        let lowestAmt = Number(sortedOpts[0].amount);

        for (let i = 1; i < sortedOpts.length; i++) {
          const amt = Number(sortedOpts[i].amount);
          if (Number.isFinite(amt) && amt < lowestAmt) {
            lowestAmt = amt;
            lowestIdx = i;
          }
        }

        sortedOpts[lowestIdx] = { ...sortedOpts[lowestIdx], amount: 0 };
        setFreeShippingID(sortedOpts[lowestIdx].id)
      }
      // If customer is local to Brownsville area, offer a free local dropoff option.
      const normZip = normalizeZip(address.zip);
      const isLocal = brownsvilleRadiusZipCodes.includes(normZip);
      let localInsertedId = null;
      if (isLocal) {
        // Create a local dropoff option (free)
        const localId = `local_dropoff_${qid}`;
        const localOption = {
          id: localId,
          label: 'Local Dropoff (Free)',
          amount: 0,
          source: 'local',
          isFreeShipping: true,
          displayAmount: 0,
        };

        // Find USPS options and remove/replace the most expensive USPS option if present
        const uspsIndexes = sortedOpts
          .map((o, i) => ({ o, i }))
          .filter(({ o }) => String(o.label || '').toUpperCase().includes('USPS') || String(o.source || '').toUpperCase().includes('USPS'));

        if (uspsIndexes.length > 0) {
          // find most expensive USPS option index among sortedOpts
          let mostExpIdx = uspsIndexes[0].i;
          let mostExpAmt = Number(uspsIndexes[0].o.amount);
          for (const { o, i } of uspsIndexes) {
            const amt = Number(o.amount);
            if (Number.isFinite(amt) && amt > mostExpAmt) {
              mostExpAmt = amt;
              mostExpIdx = i;
            }
          }
          // Replace the most expensive USPS option with a local-dropoff variant,
          // but preserve the original option id so the server-side quote still matches.
          const origId = sortedOpts[mostExpIdx].id;
          sortedOpts[mostExpIdx] = {
            ...sortedOpts[mostExpIdx],
            label: localOption.label,
            amount: localOption.amount,
            source: localOption.source,
            isFreeShipping: true,
            displayAmount: 0,
            id: origId,
          };
          localInsertedId = origId;
        } else {
          // No USPS option found; do not inject a client-only option because it would
          // not exist on the server-side quote (would be rejected later). Leave options as-is.
        }
      }
      // const sortedOpts = opts;
      setQuoteId(qid);
      setOptions(sortedOpts);
      setSelectedId(localInsertedId || sortedOpts[0].id);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || err.message || "Estimate failed");
    } finally {
      setLoading(false);
    }
  }

  async function handleProceed() {
    if (!quoteId) return setError("Please get rates first.");
    if (!selectedId) return setError("Please select a shipping option.");

    setError(null);
    setLoading(true);

    try {
      const payload = {
        items: itemsForEstimate, // minimal; server verifies
        quoteId,
        selectedShippingId: selectedId,
        // include the selected shipping object so the server can honor displayAmount/isFreeShipping
        selectedShipping: options.find(o => String(o.id) === String(selectedId)) || null,
        userId: authResponse,
        userAddress: {
          ...address,
          state: address.state.trim().toUpperCase(),
        }
      };
      const res = await axios.post(`${API_BASE_URL}/api/checkout/create-session`, payload, {
        withCredentials: true,
      });

      if (res.data?.url) window.location.href = res.data.url;
      else setError("No checkout URL returned");
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || err.message || "Checkout create failed");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    async function checkStatus() {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/auth/verify-token`,
          { withCredentials: true }
        );
        setAuthResponse(response?.data?.userId || null);
      } catch (error) {
        console.error("No user logged in: ", error);
      }
    }
    checkStatus();
  }, []);

  useEffect(() => {
    async function pullUserData() {
      try {
        const userData = await axios.get(
          `${API_BASE_URL}/api/auth/request-user`,
          { withCredentials: true }
        );
        setUserResponse(userData);
        setAddress((a) => ({ ...a, ["name"]: `${userData?.data?.firstName} ${userData?.data?.lastName}` }));
      } catch (err) {
        setUserResponse(false);
      }
    }

    pullUserData();
  }, []);

  return (
    <section className="enter-shipping-container">
      <h1>Enter shipping address</h1>
      <h3 style={{ width: "100%", textAlign: "center" }}>{userResponse == false ? "Guest Checkout" : (<>Checking out via <span>{userResponse?.data?.email}</span></>)}</h3>
      <article className="enter-shipping-form-container">

        <form onSubmit={handleEstimate}>
          <label>
            Full Name
            <input value={address.name} onChange={update("name")} />
          </label>

          <label>
            Address Line 1
            <input value={address.line1} onChange={update("line1")} />
          </label>

          <label>
            Address Line 2
            <input value={address.line2} onChange={update("line2")} placeholder="(Optional)" />
          </label>

          <div className="enter-shipping-form-row">
            <label>
              City
              <input value={address.city} onChange={update("city")} />
            </label>

            <label>
              State
              <input style={{ maxWidth: "3rem", minWidth: "2rem" }} value={address.state.toUpperCase()} onChange={update("state")} maxLength={2} />
            </label>

            <label>
              ZIP
              <input value={address.zip} onChange={update("zip")} />
            </label>
          </div>

          <div className="enter-shipping-buttons" style={{ display: "flex", gap: 8, marginTop: 6 }}>
            <button className="enter-shipping-get-rates" type="submit" disabled={loading}>
              {loading ? "Checking..." : "Get Rates"}
            </button>
            <button className="enter-shipping-back" type="button" onClick={() => navigate("/cart")} disabled={loading}>
              Back to Cart
            </button>
          </div>
        </form>
      </article>

      <article className="enter-shipping-rates-container">

        {options.length === 0 && <i>After entering your shipping address, click <a className="enter-shipping-get-rates-emphasis">"Get Rates"</a> to see available shipping options.</i>}
        {error && <div className="form-error" style={{ marginTop: 12, color: "crimson" }}>{error}</div>}

        {options.length > 0 && (
          <div className="enter-shipping-rates-options">
            <h3>Shipping Options</h3>
            <ul style={{ listStyle: "none", paddingLeft: 0 }}>
              {options.map((opt) => (
                <li key={opt.id} style={{ marginBottom: 8 }}>
                  <label>
                    <input
                      type="radio"
                      name="ship"
                      checked={selectedId === opt.id}
                      onChange={() => setSelectedId(opt.id)}
                    />{" "}
                    {opt.label} — {opt.amount == 0 ? `FREE SHIPPING!` : `$${(opt.amount / 100).toFixed(2)}`}
                  </label>
                </li>
              ))}
            </ul>

            <button className="enter-shipping-proceed" onClick={handleProceed} disabled={loading}>
              {loading ? "Redirecting..." : "Proceed to Checkout"}
            </button>
          </div>
        )}

      </article>
    </section>
  );
}
